/**
 * The study page's HTTP calls. Each one is best effort: a telemetry beacon
 * that does not land costs a line in the audit trail, never the session.
 */

import type {
  StudyCompletePayload,
  StudyHeartbeat,
  StudyParams,
  StudyPublicConfig,
  StudyTelemetryType,
} from '@overcooked/shared'

export async function fetchStudyConfig(): Promise<StudyPublicConfig> {
  const response = await fetch('/api/study/config')
  if (!response.ok) throw new Error(`config answered ${response.status}`)
  return (await response.json()) as StudyPublicConfig
}

/**
 * "I can't continue on this device", reported. The answer is the
 * participant's completion — at stage 0, the code that sends them back to
 * Prolific to return the submission. Anything else (an error status, a body
 * that is not a completion, no network) is `null`: not reported yet.
 */
export async function reportDeviceIncompatible(params: StudyParams): Promise<StudyCompletePayload | null> {
  try {
    const response = await fetch('/api/study/device-incompatible', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(params),
    })
    if (!response.ok) return null
    const result = (await response.json()) as StudyCompletePayload | null
    return result && typeof result === 'object' && typeof result.prolificRedirect === 'boolean' && typeof result.reason === 'string'
      ? result
      : null
  } catch {
    return null
  }
}

/** Where "I can't continue on this device" stands. */
export type DeviceReport =
  | { status: 'sending' }
  | { status: 'refused'; result: StudyCompletePayload }
  | { status: 'failed'; message: string }

export const DEVICE_REPORT_FAILED =
  "We couldn't reach the server, so this isn't saved yet. Check your connection and try again."

/**
 * Report the device, and refuse it on screen only once the server has the
 * report and has answered with the participant's completion. One that did
 * not land is said so, and the participant can press again.
 */
export async function reportDevice(
  params: StudyParams,
  onChange: (report: DeviceReport) => void,
  report: (params: StudyParams) => Promise<StudyCompletePayload | null> = reportDeviceIncompatible,
): Promise<void> {
  onChange({ status: 'sending' })
  const result = await report(params)
  onChange(result ? { status: 'refused', result } : { status: 'failed', message: DEVICE_REPORT_FAILED })
}

/**
 * One telemetry event. `sendBeacon` when there is one — it is the only call a
 * page being closed can still make — and a keep-alive `fetch` otherwise.
 */
export function sendTelemetry(params: StudyParams, type: StudyTelemetryType, detail?: string): void {
  const body = JSON.stringify({ ...params, type, clientAt: Date.now(), ...(detail ? { detail } : {}) })
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      if (navigator.sendBeacon('/api/study/telemetry', new Blob([body], { type: 'application/json' }))) return
    }
    void fetch('/api/study/telemetry', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => undefined)
  } catch {
    // Telemetry is never worth an error on a participant's screen.
  }
}

/**
 * Best effort, always: a 429, a 5xx, a non-JSON body or no network at all is
 * `null`, which the watchdog treats as "nothing new" and asks again on its next
 * tick. Only a heartbeat that says the server has issued a code can move the
 * page, and then only to the end screen — never a navigation from here.
 */
export async function stageHeartbeat(params: StudyParams, signal?: AbortSignal): Promise<StudyHeartbeat | null> {
  try {
    const response = await fetch('/api/study/stage-heartbeat', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(params),
      ...(signal ? { signal } : {}),
    })
    if (!response.ok) return null
    const beat = (await response.json()) as StudyHeartbeat | null
    return beat && typeof beat === 'object' && typeof beat.prolificRedirect === 'boolean' ? beat : null
  } catch {
    return null
  }
}

/** The end screen a finished participant's heartbeat stands for, or `null` while they are still going. */
export function finishedFromHeartbeat(beat: StudyHeartbeat | null): StudyCompletePayload | null {
  if (!beat || typeof beat.outcome !== 'string') return null
  return {
    completionCode: beat.prolificRedirect ? beat.completionCode : null,
    reason: beat.outcome,
    paymentStage: beat.paymentStage,
    prolificRedirect: beat.prolificRedirect,
    redirectUrl: beat.prolificRedirect ? beat.redirectUrl : null,
  }
}

/**
 * Before the tech check: has this participant already finished? Then the page
 * shows their code at once — a new tab, or a device without a microphone,
 * must not stand between somebody and the code they earned. Anything other
 * than a clear "finished" (a new participant's 404, an error, no answer
 * within `timeoutMs`) is `null`, and the page carries on as usual.
 */
export async function lookupFinished(params: StudyParams, timeoutMs = 5_000): Promise<StudyCompletePayload | null> {
  const controller = typeof AbortController === 'function' ? new AbortController() : null
  let timer: ReturnType<typeof setTimeout> | undefined
  // Aborting covers a fetch that honours it; the race covers one that does not.
  const late = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      controller?.abort()
      resolve(null)
    }, timeoutMs)
  })
  try {
    return finishedFromHeartbeat(await Promise.race([stageHeartbeat(params, controller?.signal), late]))
  } finally {
    clearTimeout(timer)
  }
}
